import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { countries } from "country-data-list";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumber } from "libphonenumber-js";
import CancelSubscriptionModal from "../../../Components/Modals/CancelSubscriptionModal";
import PauseSubscriptionModal from "../../../Components/Modals/PauseSubscriptionModal";
import LogoutAllModal from "../../../Components/Modals/LogoutAllModal";
import CurrentPasswordModal from "../../../Components/Modals/CurrentPasswordModal";
import Loading from "../../../Components/Loading/Loading";
import Translate from "../../../Components/Multilanguage/Translate";
import { getUserDetails } from "../../../Redux/UserDetailsSlice/UserDetailsSlice";
import { delay } from "../../../utils/utils";
import {
  fetchSubscriptionHistory,
  getAccountDetails,
  resumeSubscription,
  updateAccountdetails,
} from "./service";

const AccountSettings2 = () => {
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const isRTL = useSelector((state) => state?.rtl?.isRTL);

  const [accountDetails, setAccountDetails] = useState({});
  const [countryListOption, setCountryListOption] = useState([]);
  const [currentCountry, setCurrentCountry] = useState({});
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [changedValue, setChangedValue] = useState({ email_sms_consent: true });
  const [billingPreview, setBillingPreview] = useState({});
  const [cancelSubModal, setCancelSubModal] = useState(false);
  const [pauseSubModal, setPauseSubModal] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileWill, setProfileWill] = useState();
  const [isEditMode, setIsEditMode] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const appInfo = {
    projectDetails: projectInfo,
    accessToken,
  };

  const theme = projectInfo?.projectConfig?.config?.THEME;
  const websiteNewCheckout = projectInfo?.projectConfig?.config?.WEBSITE_NEW_CHECKOUT_REQUIRED === "true";
  const canChangePlan =
    projectInfo?.projectConfig?.config?.CHANGE_PLAN_REQUIRED == "true" &&
    accountDetails?.change_plan_required === true &&
    (accountDetails?.subscription_type === 3 || accountDetails?.subscription_type === 4);

  const isVizioPayment =
    accountDetails?.mode_of_payment === "Vizio" || accountDetails?.mode_of_payment === "vizio";
  const hasSubscription = !!accountDetails?.sub_id;
  const isCancelled = hasSubscription && accountDetails?.cancel_status === true;
  const isActive = hasSubscription && accountDetails?.cancel_status === false;
  const isRecurring = accountDetails?.subscription_type === 3 || accountDetails?.subscription_type === 4;
  const canCancel = isActive && accountDetails?.mode_of_payment !== "giftcard" && isRecurring;
  const canPauseOrResume = [50148, 50124].includes(projectInfo?.projectConfig?.pubid) && isActive;
  const hasEmailPreferences = Object.prototype.hasOwnProperty.call(accountDetails || {}, "email_sms_consent");
  const showPreferencesSection = hasEmailPreferences;

  const membershipStateLabel = useMemo(() => {
    if (!hasSubscription) return "current";
    return isCancelled ? "canceled" : "active";
  }, [hasSubscription, isCancelled]);

  const profileDisplayName = useMemo(() => {
    const first = values?.firstName?.trim?.() || "";
    const last = values?.lastName?.trim?.() || "";
    const fullName = `${first} ${last}`.trim();
    if (fullName) return fullName;

    const emailValue = (values?.email || accountDetails?.user_email || "").trim();
    if (!emailValue) return "User";

    const emailPrefix = emailValue.split("@")[0]?.trim();
    return emailPrefix || "User";
  }, [values?.firstName, values?.lastName, values?.email, accountDetails?.user_email]);

  const hasBillingPreview = Boolean(
    billingPreview?.date || billingPreview?.name || billingPreview?.amount
  );

  useEffect(() => {
    window.scroll(0, 0);
    fetchAccountDetails();
    fetchBillingPreview();
  }, []);

  const fetchBillingPreview = async () => {
    try {
      const response = await fetchSubscriptionHistory(appInfo);
      if (response?.status === 200) {
        const lastPurchase = response?.data?.last_purchase || {};
        const latest = response?.data?.purchase_history?.[0] || {};
        setBillingPreview({
          date: lastPurchase?.valid_from,
          name: latest?.subscription_name,
          amount: latest?.total,
        });
      }
    } catch (err) {
      setBillingPreview({});
    }
  };

  useEffect(() => {
    if (!accountDetails) return;

    setCountryListOption(
      countries?.all?.map((item, index) => {
        const { alpha2, name } = item;
        if (item?.alpha2 !== accountDetails?.country_code) {
          return (
            <option key={index} value={alpha2}>
              {name}
            </option>
          );
        }

        setCurrentCountry({ name, value: alpha2 });
        return null;
      })
    );
  }, [accountDetails]);

  const fetchAccountDetails = async (isUpdate) => {
    setLoading(true);
    try {
      if (isUpdate) {
        await delay(2500);
      }

      const response = await getAccountDetails(appInfo);
      if (response?.status === 200) {
        const details = response?.data?.data || {};

        const currentValues = {
          email: details?.user_email,
          firstName: details?.first_name,
          lastName: details?.last_name,
          phone: details?.phone,
          country: details?.country_code,
        };

        setValues((prev) => ({ ...prev, ...currentValues }));
        setChangedValue((prev) => ({
          ...prev,
          ...currentValues,
          email_sms_consent:
            details?.email_sms_consent === true || details?.email_sms_consent === false
              ? details?.email_sms_consent
              : true,
        }));
        setAccountDetails(details);

        if (isUpdate) {
          dispatch(getUserDetails({ userDetails: details }));
          fetchBillingPreview();
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message, {
        position: "bottom-center",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (event) => {
    const { name, value } = event?.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setChangedValue((prev) => ({
      ...prev,
      [name]: value,
      countryName: currentCountry?.name,
    }));
  };

  const handlePhoneUpdate = (phone) => {
    setValues((prev) => ({ ...prev, phone }));
    setChangedValue((prev) => ({
      ...prev,
      phone,
      countryName: currentCountry?.name,
    }));
  };

  const validation = () => {
    let validationStatus = true;
    const nextErrors = {};
    const nameRegex = /^[a-zA-Z ]{3,30}$/;
    const { firstName, lastName, country, phone } = values;

    if (!firstName) {
      nextErrors.firstName = <Translate textKey={"first_name_required"} />;
      validationStatus = false;
    } else if (!firstName?.match(nameRegex)) {
      nextErrors.firstName = <Translate textKey={"name_validation_message"} />;
      validationStatus = false;
    }

    if (!lastName) {
      nextErrors.lastName = <Translate textKey={"last_name_required"} />;
      validationStatus = false;
    } else if (!lastName?.match(nameRegex)) {
      nextErrors.lastName = <Translate textKey={"name_validation_message"} />;
      validationStatus = false;
    }

    if (!country) {
      nextErrors.country = <Translate textKey={"country_required"} />;
      validationStatus = false;
    }

    try {
      if (phone && phone.trim() !== "") {
        const formattedPhone = "+" + phone.replace(/^(\++)/, "");
        const phoneNumber = parsePhoneNumber(formattedPhone);
        if (!phoneNumber.isValid()) {
          nextErrors.phone = <Translate textKey={"phone_error_msg"} />;
          validationStatus = false;
        }
        setChangedValue((prev) => ({
          ...prev,
          phone: phoneNumber.formatInternational(),
        }));
      }
    } catch (e) {
      nextErrors.phone = <Translate textKey={"phone_error_msg"} />;
      validationStatus = false;
    }

    setErrors(nextErrors);
    return validationStatus;
  };

  const updateAccountDetails = async () => {
    try {
      const payload = values?.currentPassword
        ? { ...changedValue, currentPassword: values.currentPassword }
        : changedValue;
      const response = await updateAccountdetails(appInfo, payload);
      if (response?.data?.success === true) {
        toast.success(<Translate textKey={"account_updated_message"} />, {
          position: "bottom-center",
        });
        setPasswordModal(false);
        setValues((prev) => ({ ...prev, currentPassword: "" }));
        setIsEditMode(false);
        fetchAccountDetails(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message, {
        position: "bottom-center",
      });
    }
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    if (!validation()) {
      toast.error(<Translate textKey={"check_the_fields"} />, {
        position: "bottom-center",
      });
      return;
    }

    if (accountDetails?.password_exists === true) {
      setValues((prev) => ({ ...prev, currentPassword: "" }));
      setPasswordModal(true);
      return;
    }

    await updateAccountDetails();
  };

  const handleUpdateItem = (event) => {
    const { name, value } = event?.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif)$/i;
    if (!allowedExtensions.exec(file?.name)) {
      toast.error(<Translate textKey={"invalid_extension_message"} />, {
        position: "bottom-center",
      });
      return;
    }

    setChangedValue((prev) => ({ ...prev, file }));
    setProfileWill(URL.createObjectURL(file));
  };

  const handleResume = async () => {
    setLoading(true);
    try {
      const response = await resumeSubscription(
        appInfo,
        accountDetails?.sub_id,
        accountDetails?.user_subscription_id
      );

      if (response?.status === 200) {
        toast.success(<Translate textKey={"subscription_resume"} />, {
          position: "bottom-center",
        });
        setPauseSubModal(false);
        fetchAccountDetails(true);
      } else {
        toast.error(response?.data?.message);
      }
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlanClick = () => {
    if (!canChangePlan) return;

    const activeSubId = accountDetails?.sub_id;
    if (!activeSubId) {
      toast.error(<Translate textKey={"try_again_msg"} />, {
        position: "bottom-center",
      });
      return;
    }

    const upgradeState = {
      type: "upgrade",
      subId: activeSubId,
      userSubscriptionId: accountDetails?.user_subscription_id,
      prevPath: window.location.pathname,
    };

    if (websiteNewCheckout) {
      navigate("/checkout", { state: upgradeState });
      return;
    }

    navigate("/subscription", { state: upgradeState });
  };

  const handleMarketingToggle = async (checked) => {
    setChangedValue((prev) => ({ ...prev, email_sms_consent: checked }));
    try {
      const response = await updateAccountdetails(appInfo, {
        email_sms_consent: checked,
      });
      if (response?.data?.success !== true) {
        throw new Error(response?.data?.message);
      }
      fetchAccountDetails(true);
    } catch (err) {
      toast.error(err?.message, {
        position: "bottom-center",
      });
      setChangedValue((prev) => ({
        ...prev,
        email_sms_consent: !checked,
      }));
    }
  };

  if (loading) {
    return <Loading />;
  }

  const renderProfileAvatar = () => {
    if (profileWill) {
      return <img src={profileWill} alt="" />;
    }

    if (accountDetails?.user_image) {
      return <img src={accountDetails?.user_image} alt="" />;
    }

    return (
      <div className="avatar-fallback">
        {(profileDisplayName?.[0] || "U").toUpperCase()}
      </div>
    );
  };

  const billingPreviewDate = billingPreview?.date;
  const billingPreviewName = billingPreview?.name;
  const billingPreviewAmount = billingPreview?.amount;

  return (
    <div className={`accountSettings2 ${isRTL ? "rtl" : ""}`}>
      {passwordModal && (
        <CurrentPasswordModal
          show={passwordModal}
          setPasswordModal={setPasswordModal}
          updateAccountDetails={updateAccountDetails}
          values={values}
          setValues={setValues}
          errors={errors}
          setErrors={setErrors}
          handleUpdateItem={handleUpdateItem}
        />
      )}

      {cancelSubModal && (
        <CancelSubscriptionModal
          fetchAccountDetails={fetchAccountDetails}
          setCancelSubModal={setCancelSubModal}
          subId={accountDetails?.sub_id}
          userSubscriptionId={accountDetails?.user_subscription_id}
          pauseStatus={accountDetails?.pause_status}
          setPauseSubModal={setPauseSubModal}
          newUi={true}
        />
      )}

      {pauseSubModal && (
        <PauseSubscriptionModal
          fetchAccountDetails={fetchAccountDetails}
          setPauseSubModal={setPauseSubModal}
          subId={accountDetails?.sub_id}
          userSubscriptionId={accountDetails?.user_subscription_id}
        />
      )}

      {logoutModal && (
        <LogoutAllModal
          setLogoutModal={setLogoutModal}
          modalType={{
            type: "logoutAll",
            heading: <Translate textKey={"logout_all_text"} />,
          }}
        />
      )}

      <div className="wrapper">
        <div className="accountSettings2__head">
          <span className="eyebrow"><Translate textKey={"my_account"} /></span>
          <h1><Translate textKey={"account_and_subscription"} /></h1>
        </div>

        <section className="accountSettings2__section">
          <div className="section-title"><Translate textKey={"profile"} /></div>
          <div className="profile-card">
            <div className="profile-card__left">
              <div className="avatar-wrap">{renderProfileAvatar()}</div>
              <div className="profile-info">
                <div className="profile-name-row">
                  <h2>{profileDisplayName}</h2>
                  <button
                    type="button"
                    className="profile-edit-trigger"
                    onClick={() => setIsEditMode((prev) => !prev)}
                  >
                    {isEditMode ? <Translate textKey={"close"} /> : <Translate textKey={"edit"} />}
                  </button>
                </div>
                <p>{values?.email || ""}</p>
              </div>
            </div>
          </div>

          {isEditMode && (
            <div className="profile-form-shell">
              <form onSubmit={onSubmitHandler}>
                <div className="edit-avatar-row">
                  <div className="edit-avatar-actions">
                    <label htmlFor="file" className="avatar-edit" aria-label="Edit profile picture">
                      <Translate textKey={"change_picture"} />
                    </label>
                    {accountDetails?.password_exists === true ? (
                      <Link to="/change-password" className="avatar-edit">
                        <Translate textKey={"change_password"} />
                      </Link>
                    ) : (
                      <Link to="/set-password" state={accountDetails} className="avatar-edit">
                        <Translate textKey={"set_password"} />
                      </Link>
                    )}
                    <input type="file" id="file" accept="image/*" onChange={handleFileChange} />
                  </div>
                </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="firstName">
                    <Translate textKey={"first_name"} />
                  </label>
                  <input id="firstName" name="firstName" value={values?.firstName || ""} onChange={handleUpdate} />
                  {errors?.firstName && <span className="error">{errors?.firstName}</span>}
                </div>
                <div className="field">
                  <label htmlFor="lastName">
                    <Translate textKey={"last_name"} />
                  </label>
                  <input id="lastName" name="lastName" value={values?.lastName || ""} onChange={handleUpdate} />
                  {errors?.lastName && <span className="error">{errors?.lastName}</span>}
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="email">
                    <Translate textKey={"email"} />
                  </label>
                  <input id="email" name="email" value={values?.email || ""} disabled />
                </div>
                <div className="field field-phone">
                  <label htmlFor="phone">
                    <Translate textKey={"phone"} />
                  </label>
                  <PhoneInput
                    enableSearch={true}
                    className="phoneInput"
                    country={"us"}
                    value={values?.phone || ""}
                    onChange={handlePhoneUpdate}
                    inputProps={{
                      name: "phone",
                      id: "phone",
                    }}
                    containerClass="phone-input-responsive"
                    inputStyle={{ width: "100%", height: "52px" }}
                  />
                  {errors?.phone && <span className="error">{errors?.phone}</span>}
                </div>
              </div>

              <div className="field-row country-row">
                <div className="field">
                  <label htmlFor="country">
                    <Translate textKey={"country"} />
                  </label>
                  <select id="country" name="country" onChange={handleUpdate}>
                    <option value={currentCountry?.value}>{currentCountry?.name}</option>
                    {countryListOption}
                  </select>
                  {errors?.country && <span className="error">{errors?.country}</span>}
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className={`cta cta--solid ${theme === "LIGHT" ? "light-theme" : ""}`}>
                  <Translate textKey={"save_changes"} />
                </button>
              </div>
              </form>
            </div>
          )}
        </section>

        {(isVizioPayment || hasSubscription) && (
          <section className="accountSettings2__section">
            <div className="section-title"><Translate textKey={"your_plan"} /></div>

            <div className={`plan-card state-${membershipStateLabel}`}>
              {isVizioPayment && (
                <div className="vizio-note">
                  <Translate textKey={"vizio_subscription_message"} />
                  <span onClick={() => window.open("https://www.vizio.com/en/vizio-account", "_blank")}>
                    https://www.vizio.com/en/vizio-account
                  </span>
                </div>
              )}

              {hasSubscription && (
                <>
                  <div className="plan-card__header">
                    <div>
                      <h3>{accountDetails?.subscription_name || <Translate textKey={"subscription_title"} />}</h3>
                      {isCancelled && (
                        <p>
                          <Translate textKey={"subscription_cancelled"} />
                        </p>
                      )}
                    </div>
                    <span className={`pill ${isCancelled ? "pill--warning" : "pill--active"}`}>
                      {isCancelled ? <Translate textKey={"subscription_cancelled"} /> : <Translate textKey={"active"} />}
                    </span>
                  </div>

                  <div className="plan-actions">
                    {isCancelled ? (
                      <button
                        className="cta cta--solid"
                        type="button"
                        onClick={() => navigate(websiteNewCheckout ? "/checkout" : "/subscription")}
                      >
                        <Translate textKey={"resubscribe"} />
                      </button>
                    ) : (
                      <>
                        {canChangePlan && (
                          <button className="cta cta--plan" type="button" onClick={handleChangePlanClick}>
                            <Translate textKey={"change_plan_caps"} />
                          </button>
                        )}

                        {canCancel && (
                          <button className="cta cta--danger" type="button" onClick={() => setCancelSubModal(true)}>
                            <Translate textKey={"cancel_plan"} />
                          </button>
                        )}

                        {canPauseOrResume && (
                          <button
                            className="cta cta--ghost"
                            type="button"
                            onClick={() => (accountDetails?.pause_status === false ? setPauseSubModal(true) : handleResume())}
                          >
                            {accountDetails?.pause_status === false ? (
                              <Translate textKey={"pause_subscription"} />
                            ) : (
                              <Translate textKey={"resume_subscription"} />
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {hasBillingPreview && (
          <section className="accountSettings2__section">
            <div className="section-title"><Translate textKey={"billing_history"} /></div>
            <div className="history-card">
              <div className="history-row">
                <span>{billingPreviewDate}</span>
                <span>{billingPreviewName}</span>
                <span>{billingPreviewAmount}</span>
                <Link to="billing-activity"><Translate textKey={"billing_details_caps"} /></Link>
              </div>
            </div>
          </section>
        )}

        {showPreferencesSection && (
          <section className="accountSettings2__section">
            <div className="section-title"><Translate textKey={"settings_caps"} /></div>
            <div className="prefs-card">
              {hasEmailPreferences && (
                <div className="pref-row">
                  <div>
                    <h4><Translate textKey={"email"} /></h4>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={!!changedValue?.email_sms_consent}
                      onChange={(event) => handleMarketingToggle(event.target.checked)}
                    />
                    <span className="slider" />
                  </label>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="accountSettings2__section">
          <div className="section-title"><Translate textKey={"settings_caps"} /></div>
          <div className="actions-card">
            <Link to="streaming-activity" className="action-row">
              <span>
                <Translate textKey={"recent_device_streaming_activity"} />
              </span>
              <span>&gt;</span>
            </Link>
            <button type="button" className="action-row" onClick={() => setLogoutModal(true)}>
              <span>
                <Translate textKey={"sign_out_from_all_devices"} />
              </span>
              <span>&gt;</span>
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AccountSettings2;